import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Mail, MessageSquare, Send, CheckCircle2, XCircle, Eye, X, Loader2, ShieldCheck } from "lucide-react";

const s = (v, fb = "—") => (v == null || v === "" ? fb : (typeof v === "object" ? fb : v));

// keep in sync with backend _render / _resolve_recipient
const renderTpl = (text, lead) => {
    const ctx = {
        contact_name: (lead?.first_name || lead?.last_name || "there").toString().trim() || "there",
        company: (lead?.company_name || "your company").toString().trim() || "your company",
        agent_name: (lead?.sales_agent_name || lead?.owner_name || "your DK Homestyle consultant").toString().trim(),
        store: (lead?.store || "DK Homestyle").toString().trim() || "DK Homestyle",
        email: lead?.email || "",
        phone: lead?.phone || lead?.mobile || "",
    };
    return String(text || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in ctx ? ctx[k] : m));
};
const validEmail = (e) => {
    e = (e || "").trim().toLowerCase();
    if (!e || ["n/a", "na", "none", "-"].includes(e)) return false;
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
};
const validMobile = (p) => {
    let d = (p || "").replace(/\D/g, "");
    if (d.startsWith("63") && d.length === 12) d = "0" + d.slice(2);
    else if (d.startsWith("9") && d.length === 10) d = "0" + d;
    return d.length === 11 && d.startsWith("09") ? d : null;
};

export default function Outreach() {
    const [channel, setChannel] = useState("email");
    const [facets, setFacets] = useState({ agents: [], stores: [] });
    const [flt, setFlt] = useState({ status: "", store: "", agent: "", aging: "" });
    const [tpl, setTpl] = useState({ emailSubject: "", emailBody: "", smsBody: "" });

    const [sampleLeads, setSampleLeads] = useState([]);
    const [matched, setMatched] = useState(0);
    const [sampleId, setSampleId] = useState("");

    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [resFilter, setResFilter] = useState("all");
    const [viewMsg, setViewMsg] = useState(null);

    // load templates + facets
    useEffect(() => {
        (async () => {
            try {
                const [t, f] = await Promise.all([
                    api.get("/crm/outreach/templates"),
                    api.get("/crm/leads/facets"),
                ]);
                setTpl({
                    emailSubject: t.data?.email?.subject || "",
                    emailBody: t.data?.email?.body || "",
                    smsBody: t.data?.sms?.body || "",
                });
                setFacets({ agents: f.data?.agents || [], stores: f.data?.stores || [] });
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    // load matched count + a few sample leads for preview whenever filters change
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/crm/leads", {
                    params: {
                        status: flt.status || undefined, store: flt.store || undefined,
                        agent: flt.agent || undefined, aging: flt.aging || undefined, limit: 25,
                    },
                });
                const items = data?.items || [];
                setSampleLeads(items);
                setMatched(typeof data?.total === "number" ? data.total : items.length);
                setSampleId((prev) => (items.find((l) => l.id === prev) ? prev : (items[0]?.id || "")));
            } catch { /* ignore */ }
        })();
    }, [flt]);

    const sample = useMemo(() => sampleLeads.find((l) => l.id === sampleId) || sampleLeads[0], [sampleLeads, sampleId]);

    // live preview (client-side render, matches backend)
    const preview = useMemo(() => {
        if (!sample) return null;
        if (channel === "email") {
            const to = (sample.email || "").trim();
            const ok = validEmail(to);
            return {
                to, ok,
                reason: ok ? "Would deliver to inbox" : (to ? "Invalid email format" : "No email address on file"),
                subject: renderTpl(tpl.emailSubject, sample),
                body: renderTpl(tpl.emailBody, sample),
            };
        }
        const raw = (sample.phone || sample.mobile || "").trim();
        const norm = validMobile(raw);
        return {
            to: norm || raw, ok: Boolean(norm),
            reason: norm ? "Would deliver to handset" : (raw ? "Not a valid PH mobile number" : "No mobile number on file"),
            subject: "",
            body: renderTpl(tpl.smsBody, sample),
        };
    }, [sample, channel, tpl]);

    const runSimulation = async () => {
        setRunning(true);
        setResult(null);
        try {
            const { data } = await api.post("/crm/outreach/simulate", {
                channel,
                subject: channel === "email" ? tpl.emailSubject : undefined,
                body: channel === "email" ? tpl.emailBody : tpl.smsBody,
                status: flt.status || undefined, store: flt.store || undefined,
                agent: flt.agent || undefined, aging: flt.aging || undefined,
            });
            setResult(data);
            setResFilter("all");
            toast.success(`Simulated ${data.processed} ${channel === "email" ? "emails" : "SMS"} — ${data.sent} sent, ${data.failed} failed`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRunning(false);
        }
    };

    const shownResults = (result?.results || []).filter((r) => resFilter === "all" || r.status === resFilter);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-outreach-page">
            <div>
                <div className="sc-section-title">CRM</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Outreach Simulator</h1>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    Simulation only — no real emails or SMS are sent. Use it to check which leads are actually reachable.
                </p>
            </div>

            {/* channel */}
            <div className="inline-flex rounded-md border border-border overflow-hidden">
                {[["email", "Email", Mail], ["sms", "SMS", MessageSquare]].map(([val, label, Icon]) => (
                    <button key={val} onClick={() => setChannel(val)}
                        className={`px-4 py-1.5 text-sm flex items-center gap-1.5 ${val !== "email" ? "border-l border-border" : ""} ${channel === val ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                        data-testid={`outreach-channel-${val}`}>
                        <Icon className="w-4 h-4" /> {label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* LEFT: audience + template */}
                <div className="space-y-4">
                    <div className="sc-card p-4 space-y-3">
                        <div className="text-sm font-semibold">Audience</div>
                        <div className="grid grid-cols-2 gap-2">
                            <select className="sc-input" value={flt.agent} onChange={(e) => setFlt({ ...flt, agent: e.target.value })}>
                                <option value="">All agents</option>
                                {facets.agents.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                            <select className="sc-input" value={flt.store} onChange={(e) => setFlt({ ...flt, store: e.target.value })}>
                                <option value="">All stores</option>
                                {facets.stores.map((st) => <option key={st} value={st}>{st}</option>)}
                            </select>
                            <select className="sc-input" value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })}>
                                <option value="">Open leads</option>
                                {["New", "Contacted", "Qualified", "Not Interested"].map((st) => <option key={st} value={st}>{st}</option>)}
                            </select>
                            <select className="sc-input" value={flt.aging} onChange={(e) => setFlt({ ...flt, aging: e.target.value })}>
                                <option value="">Any age</option>
                                <option value="fresh">Fresh (≤30d)</option>
                                <option value="aging">Aging (31–90d)</option>
                                <option value="stale">Stale (90+d)</option>
                            </select>
                        </div>
                        <div className="text-sm">
                            <span className="font-semibold text-base">{matched.toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</span>
                            <span className="text-muted-foreground"> leads match this audience</span>
                        </div>
                    </div>

                    <div className="sc-card p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{channel === "email" ? "Email Pitch Template" : "SMS Pitch Template"}</div>
                            <div className="text-[11px] text-muted-foreground">Tiles & Surfaces</div>
                        </div>
                        {channel === "email" && (
                            <div>
                                <label className="text-xs font-medium">Subject</label>
                                <input className="sc-input mt-1" value={tpl.emailSubject}
                                    onChange={(e) => setTpl({ ...tpl, emailSubject: e.target.value })} />
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium">Message</label>
                            <textarea className="sc-input mt-1 h-auto py-2 font-mono text-xs leading-relaxed" rows={channel === "email" ? 14 : 5}
                                value={channel === "email" ? tpl.emailBody : tpl.smsBody}
                                onChange={(e) => setTpl(channel === "email" ? { ...tpl, emailBody: e.target.value } : { ...tpl, smsBody: e.target.value })} />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {["contact_name", "company", "agent_name", "store"].map((p) => (
                                <span key={p} className="sc-pill bg-secondary text-xs font-mono">{`{{${p}}}`}</span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* RIGHT: preview */}
                <div className="sc-card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold">Preview</div>
                        <select className="sc-input h-8 max-w-[60%] text-xs" value={sampleId} onChange={(e) => setSampleId(e.target.value)}>
                            {sampleLeads.length === 0 && <option>No leads match</option>}
                            {sampleLeads.map((l) => (
                                <option key={l.id} value={l.id}>
                                    {(`${l.first_name || ""} ${l.last_name || ""}`.trim() || l.company_name || "Lead")}
                                </option>
                            ))}
                        </select>
                    </div>

                    {preview && (
                        <>
                            <div className={`flex items-center gap-2 text-xs rounded-md px-3 py-2 ${preview.ok ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"}`}>
                                {preview.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                <span className="font-medium">{preview.ok ? "Would send" : "Would fail"}</span>
                                <span className="opacity-80">· {preview.reason}</span>
                            </div>

                            {channel === "email" ? (
                                <div className="rounded-lg border border-border overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-border bg-secondary/40 text-xs space-y-0.5">
                                        <div><span className="text-muted-foreground">To:</span> {preview.to || <span className="italic text-rose-500">no address</span>}</div>
                                        <div><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{preview.subject}</span></div>
                                    </div>
                                    <div className="p-4 text-sm whitespace-pre-wrap leading-relaxed max-h-[420px] overflow-y-auto">{preview.body}</div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-xs text-muted-foreground">To: {preview.to || <span className="italic text-rose-500">no number</span>}</div>
                                    <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-sky-500 text-white px-4 py-2.5 text-sm whitespace-pre-wrap">{preview.body}</div>
                                    <div className="text-[11px] text-muted-foreground text-right">{preview.body.length} chars · {Math.ceil(preview.body.length / 160)} SMS segment(s)</div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* action */}
            <div className="flex items-center gap-3">
                <button onClick={runSimulation} disabled={running || matched === 0}
                    className="h-10 px-5 rounded-md bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="outreach-run">
                    {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Simulate {channel === "email" ? "Email" : "SMS"} to {matched.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} leads
                </button>
                <span className="text-xs text-muted-foreground">Renders every message and records sent/failed — nothing leaves the system.</span>
            </div>

            {/* results */}
            {result && (
                <div className="sc-card overflow-hidden">
                    <div className="p-4 border-b border-border grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Stat label="Processed" value={result.processed} />
                        <Stat label="Sent" value={result.sent} cls="text-emerald-600" />
                        <Stat label="Failed" value={result.failed} cls="text-rose-600" />
                        <Stat label="Reachable" value={result.processed ? `${Math.round(result.sent / result.processed * 100)}%` : "—"} />
                    </div>

                    {result.reasons?.length > 0 && (
                        <div className="px-4 py-3 border-b border-border flex flex-wrap gap-2">
                            {result.reasons.map((r) => (
                                <span key={r.reason} className="sc-pill bg-secondary text-xs">{r.reason}: <b className="ml-1">{r.count}</b></span>
                            ))}
                        </div>
                    )}

                    <div className="px-4 py-2 border-b border-border flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Show:</span>
                        {[["all", "All"], ["sent", "Sent"], ["failed", "Failed"]].map(([v, l]) => (
                            <button key={v} onClick={() => setResFilter(v)}
                                className={`px-2 py-1 rounded ${resFilter === v ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}>{l}</button>
                        ))}
                        {result.capped && <span className="ml-auto text-muted-foreground">showing first {result.results.length} of {result.processed}</span>}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[720px]">
                            <thead><tr>
                                <th>Lead</th><th>To</th><th>Status</th><th>Reason</th><th></th>
                            </tr></thead>
                            <tbody>
                                {shownResults.map((r) => (
                                    <tr key={r.id}>
                                        <td>
                                            <div className="font-medium text-sm">{s(r.lead_name)}</div>
                                            <div className="text-xs text-muted-foreground">{s(r.company, "")}</div>
                                        </td>
                                        <td className="text-xs font-mono">{s(r.to, "—")}</td>
                                        <td>
                                            {r.status === "sent"
                                                ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><CheckCircle2 className="w-3.5 h-3.5" />Sent</span>
                                                : <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-medium"><XCircle className="w-3.5 h-3.5" />Failed</span>}
                                        </td>
                                        <td className="text-xs text-muted-foreground">{s(r.reason)}</td>
                                        <td className="text-right">
                                            <button onClick={() => setViewMsg(r)} className="h-7 px-2 rounded border border-border text-xs inline-flex items-center gap-1 hover:bg-secondary">
                                                <Eye className="w-3.5 h-3.5" /> View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {shownResults.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">Nothing to show.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {viewMsg && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setViewMsg(null)}>
                    <div className="sc-card w-full max-w-xl bg-background max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-background">
                            <div className="flex items-center gap-2">
                                {viewMsg.channel === "email" ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                                <span className="font-semibold text-sm">{s(viewMsg.lead_name)}</span>
                                {viewMsg.status === "sent"
                                    ? <span className="text-xs text-emerald-600">Sent</span>
                                    : <span className="text-xs text-rose-600">Failed · {viewMsg.reason}</span>}
                            </div>
                            <button onClick={() => setViewMsg(null)} className="p-1 rounded hover:bg-secondary text-muted-foreground"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-4">
                            <div className="text-xs text-muted-foreground mb-2">To: {s(viewMsg.to, "—")}</div>
                            {viewMsg.channel === "email" && <div className="text-sm font-medium mb-3">{s(viewMsg.subject)}</div>}
                            <div className="text-sm whitespace-pre-wrap leading-relaxed">{s(viewMsg.body)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, cls = "" }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-xl font-semibold ${cls}`}>{value}</div>
        </div>
    );
}
